/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  Heading,
  HStack,
  Image,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import { IPlan } from '@partiu/api/dashboard/types';
import { useAPICallAsQuery } from '@partiu/api/hooks';
import { getAgreementPlans } from '@partiu/api/subscription/api';
import { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import { Button } from '../../../../../../packages/ui/components/atoms/Button';
import { ExternalLink } from '../../../../../../packages/ui/components/atoms/ExternalLink';
import { useProfile } from '../../../../../../packages/ui/contexts/ProfileProvider';
import { useTheme } from '../../../../../../packages/ui/contexts/ThemeProvider';

export const badges = [
  { id: 1, name: 'Bronze', level: 1, image: 'badges/bronze.png' },
  { id: 2, name: 'Prata', level: 2, image: 'badges/prata.png' },
  { id: 71, name: 'Ouro', level: 3, image: 'badges/ouro.png' },
  { id: 4, name: 'Diamante', level: 5, image: 'badges/diamante.png' },
  { id: 6, name: 'Safira', level: 4, image: 'badges/safira.png' },
];

function Feature({ children }: PropsWithChildren) {
  return (
    <HStack width={'100%'}>
      <Box minWidth={'30px'}>
        <FaCheck color="green" fontSize={'18px'} />
      </Box>
      <Text>{children}</Text>
    </HStack>
  );
}

export function Plans() {
  const { id: idTheme, colors, product, getValidSubscriptions, bookstoreTypes, bookstoreLabels } = useTheme();

  const id = localStorage.getItem('agreementId') || idTheme;

  const { profile, subscriptions } = useProfile();
  const [terms, setTerms] = useState(false);
  const { data: plans } = useAPICallAsQuery('plans', getAgreementPlans, { agreementID: id?.toString() || '0' });
  const openVindi = useCallback((plan: IPlan) => {
    window.open(plan.vindiPage + '?payload=' + plan.vindiPayload, '_blank');
  }, []);
  const disabledLevel = useMemo(() => {
    if (!profile) return -1;
    const validSubscriptions = getValidSubscriptions(subscriptions);

    if (product) {
      return validSubscriptions.find((subscription: any) => subscription.plan.product === product?.id)?.plan.level;

    } else {
      if (validSubscriptions.length === 0) return -1;

      return validSubscriptions[0].plan.level;
    }
  }, [profile, subscriptions, getValidSubscriptions]);

  const plansProduct: any = product ? product.plans : plans;
  if (!plansProduct || plansProduct.length === 0) return <CircularProgress isIndeterminate color={'black'} />;

  const isMagazine = product ? product?.metadata?.hasmagazine?.value === 'true' : bookstoreTypes.includes('magazine');
  const isMídia = isMagazine ? false : product ? product.defaultProduct.isBookstore : bookstoreTypes.includes('book');

  return (
    <VStack spacing={5} paddingTop={'20px'} align={'flex-start'} width="100%">
      <Heading fontSize={'21px'}>
        Conheça nossos produtos:
      </Heading>
      <Tabs orientation="horizontal" width="100%" variant="soft-rounded" overflow="auto">
        <TabList overflowX="auto" gap={0}>
          {plansProduct
            .sort((a: any, b: any) => a.level - b.level)
            .map((p: any) => (
              <Tab
                textOverflow={'ellipsis'}
                flexGrow={1}
                whiteSpace={'nowrap'}
                padding={'10px 15px'}
                _selected={{ color: 'white', backgroundColor: colors.secondaryColor }}
                key={p.id}
              >
                {p.name}
              </Tab>
            ))}
        </TabList>
        <TabPanels>
          {plansProduct
            .sort((a: any, b: any) => a.level - b.level)
            .map((p: any) => (
              <TabPanel padding={'20px 0px'} key={p.id}>
                <VStack w={'100%'} align={'flex-start'}>
                  <Box margin="auto">
                    {badges.find((b) => b.level === p.level) && (
                      <Image src={`/${badges.find((b) => b.level === p.level)?.image}`} alt={p.name} height={'130px'} />
                    )}
                  </Box>
                  <Heading alignSelf={'center'} fontSize={'16px'} fontWeight={600} paddingTop={'10px'}>
                    Categoria {p.name}
                  </Heading>
                  <Text alignSelf={'center'} fontSize={'14px'} color={'gray.500'}>
                    {p.name} — R$ {p?.value?.replace?.('.', ',')}/mês
                  </Text>
                  <Divider marginTop={'10px'} />
                  <VStack paddingTop={'20px'} spacing={3}>
                    {isMagazine && (
                      <Feature>Aquisição mensal de {p.magazineQuantity} {p.magazineQuantity > 1 ? 'revistas digitais' : 'revista digital'} à escolha do usuário.</Feature>
                    )}
                    {isMídia && p.audiobookQuantity > 0 && (
                      <Feature>
                        Aquisição mensal de {p.bookQuantity} {p.bookQuantity > 1 ? 'ebooks digitais' : 'ebook digital'} e {p.audiobookQuantity}{' '}
                        {p.audiobookQuantity > 1 ? 'audiobooks' : 'audiobook'} à escolha do usuário.
                      </Feature>
                    )}
                    {isMídia && (!p.audiobookQuantity || p.audiobookQuantity === 0) && p.bookQuantity > 0 && (
                      <Feature>Aquisição mensal de {p.bookQuantity} {p.bookQuantity > 1 ? 'ebooks digitais' : 'ebook digital'} à escolha do usuário.</Feature>
                    )}
                    {!isMídia && !isMagazine && p.bookQuantity > 0 && (
                      <Feature>Aquisição mensal de {p.bookQuantity} {p.bookQuantity > 1 ? 'ebooks digitais' : 'ebook digital'} à escolha do usuário.</Feature>
                    )}
                    <Heading fontSize={'14px'} fontWeight={600} w={'100%'}>Inclui:</Heading>
                    <Feature>Seleção de obras disponíveis no catálogo.</Feature>
                    {isMagazine ? (
                      <Feature>Identificação editorial com ISSN.</Feature>
                    ) : (
                      <Feature>Identificação editorial com ISBN.</Feature>
                    )}
                    <Feature>Download definitivo para uso pessoal.</Feature>
                    <Feature>Acesso à biblioteca digital da plataforma para leitura online.</Feature>
                    <Feature>{p.accessAmount} {p.accessAmount > 1 ? 'acessos' : 'acesso'} para leitura online.</Feature>
                    <Feature>
                      Possibilidade de substituir uma obra do mês por outro título do catálogo.
                    </Feature>
                  </VStack>
                  <Checkbox
                    isChecked={terms}
                    onChange={(e) => setTerms(e.target.checked)}
                    marginTop={'30px'}
                    marginBottom={'10px'}
                  >
                    Estou de acordo com os{' '}
                    <ExternalLink to={'/legal/termos-de-uso'} target="blank">
                      Termos de Uso
                    </ExternalLink>
                  </Checkbox>

                  {((product && p.level?.rank === disabledLevel) || (!product && p.level === disabledLevel)) ? (
                    <Button isDisabled>Este é seu produto atual</Button>
                  ) : (
                    <Button onClick={() => openVindi(p)} isDisabled={!terms}>
                      Adquirir
                    </Button>
                  )}
                </VStack>
              </TabPanel>
            ))}
        </TabPanels>
      </Tabs>
    </VStack>
  );
}
